import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, Activity, Radio, TrendingUp, Users, Wifi,
  Monitor, RefreshCw, Play, Pause, Download, Upload
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LiveTrafficMonitor = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('customer'); // customer or ip
  const [isLive, setIsLive] = useState(true);
  const [trafficData, setTrafficData] = useState([]);
  const [currentStats, setCurrentStats] = useState({
    total_download: 0,
    total_upload: 0,
    total_bandwidth: 0,
    active_connections: 0
  });
  const [customerData, setCustomerData] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const intervalRef = useRef(null);

  // Colors for charts
  const COLORS = {
    download: '#ef4444', // red
    upload: '#eab308', // yellow
    total: '#ec4899', // pink
    customer1: '#3b82f6',
    customer2: '#10b981',
    customer3: '#8b5cf6',
    customer4: '#f59e0b',
    customer5: '#06b6d4'
  };

  useEffect(() => {
    // Initial fetch
    fetchLiveTraffic();

    // Start auto-refresh if live
    if (isLive) {
      intervalRef.current = setInterval(() => {
        fetchLiveTraffic();
      }, 3000); // Update every 3 seconds
    }

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isLive, viewMode]);

  const fetchLiveTraffic = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(
        `${API_URL}/api/monitoring/live-traffic?view=${viewMode}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Add timestamp to the data
        const timestamp = new Date().toLocaleTimeString();
        const newDataPoint = {
          time: timestamp,
          download: data.current_stats.total_download,
          upload: data.current_stats.total_upload,
          total: data.current_stats.total_bandwidth
        };

        // Add customer-specific data
        if (viewMode === 'customer' && data.customer_breakdown) {
          data.customer_breakdown.forEach((customer, index) => {
            newDataPoint[`customer_${customer.customer_id}`] = customer.total_mbps;
          });
        }

        // Keep only last 20 data points (1 minute of data)
        setTrafficData(prev => {
          const updated = [...prev, newDataPoint];
          return updated.slice(-20);
        });

        setCurrentStats(data.current_stats);
        setCustomerData(data.customer_breakdown || []);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error fetching live traffic:', error);
    }
  };

  const toggleLive = () => {
    setIsLive(!isLive);
    if (!isLive) {
      fetchLiveTraffic();
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['Time', 'Download (Mbps)', 'Upload (Mbps)', 'Total (Mbps)'],
      ...trafficData.map(d => [d.time, d.download, d.upload, d.total])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `live-traffic-${new Date().toISOString()}.csv`;
    a.click();
    toast.success('Traffic data exported!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-3">
      {/* Ultra Compact Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-lg shadow-md overflow-hidden mb-3">
        <div className="p-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/monitoring-dashboard')}
                size="sm"
                className="bg-white/20 hover:bg-white/30 text-white border border-white/30 h-7 px-2"
              >
                <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <div className="bg-white/20 backdrop-blur-sm rounded p-1.5">
                  <Radio className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-white flex items-center gap-2">
                    Live Traffic Monitor
                    {isLive && (
                      <span className="text-xs bg-red-500 px-1.5 py-0.5 rounded-full animate-pulse flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-white rounded-full animate-ping" />
                        LIVE
                      </span>
                    )}
                  </h1>
                  <p className="text-xs text-white/80">
                    Updates every 3s
                  </p>
                </div>
              </div>
            </div>

            {/* Ultra Compact Controls */}
            <div className="flex gap-1.5">
              <Button
                onClick={toggleLive}
                size="sm"
                className={`${isLive ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'} text-white h-7 px-2.5 text-xs`}
              >
                {isLive ? (
                  <>
                    <Pause className="h-3.5 w-3.5 mr-1" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="h-3.5 w-3.5 mr-1" />
                    Resume
                  </>
                )}
              </Button>
              <Button
                onClick={handleExport}
                size="sm"
                className="bg-white/20 hover:bg-white/30 text-white border border-white/30 h-7 px-2.5 text-xs"
              >
                <Download className="h-3.5 w-3.5 mr-1" />
                Export
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Ultra Compact View Mode Toggle */}
      <div className="mb-2 flex gap-2">
        <Button
          onClick={() => setViewMode('customer')}
          size="sm"
          className={`flex-1 py-2 text-xs ${
            viewMode === 'customer'
              ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
              : 'bg-white text-gray-700 border border-gray-300'
          }`}
        >
          <Users className="h-3.5 w-3.5 mr-1.5" />
          Customer View
        </Button>
        <Button
          onClick={() => setViewMode('ip')}
          size="sm"
          className={`flex-1 py-2 text-xs ${
            viewMode === 'ip'
              ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
              : 'bg-white text-gray-700 border border-gray-300'
          }`}
        >
          <Wifi className="h-3.5 w-3.5 mr-1.5" />
          IP View
        </Button>
      </div>

      {/* Ultra Compact Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 font-medium">Download</p>
                <p className="text-xl font-bold text-red-600">
                  {currentStats.total_download.toFixed(1)}
                </p>
                <p className="text-xs text-gray-500">Mbps</p>
              </div>
              <Download className="h-6 w-6 text-red-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 font-medium">Upload</p>
                <p className="text-xl font-bold text-yellow-600">
                  {currentStats.total_upload.toFixed(1)}
                </p>
                <p className="text-xs text-gray-500">Mbps</p>
              </div>
              <Upload className="h-6 w-6 text-yellow-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 font-medium">Total</p>
                <p className="text-xl font-bold text-purple-600">
                  {currentStats.total_bandwidth.toFixed(1)}
                </p>
                <p className="text-xs text-gray-500">Mbps</p>
              </div>
              <Activity className="h-6 w-6 text-purple-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 font-medium">Active</p>
                <p className="text-xl font-bold text-blue-600">
                  {currentStats.active_connections}
                </p>
                <p className="text-xs text-gray-500">online</p>
              </div>
              <Users className="h-6 w-6 text-blue-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ultra Compact Main Traffic Chart */}
      <Card className="mb-2 shadow-md border border-green-200">
        <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-600 p-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-white rounded p-1">
                <TrendingUp className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-sm font-bold text-white">
                  Real-Time Traffic Graph
                </CardTitle>
                <p className="text-xs text-white/80">
                  {lastUpdate.toLocaleTimeString()}
                </p>
              </div>
            </div>
            {isLive && (
              <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm rounded px-2 py-0.5">
                <RefreshCw className="h-3 w-3 text-white animate-spin" />
                <span className="text-white text-xs font-medium">Updating</span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trafficData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                {/* Download - Vibrant Green Gradient */}
                <linearGradient id="downloadGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.9}/>
                  <stop offset="50%" stopColor="#059669" stopOpacity={0.6}/>
                  <stop offset="95%" stopColor="#047857" stopOpacity={0.2}/>
                </linearGradient>
                
                {/* Upload - Electric Blue Gradient */}
                <linearGradient id="uploadGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.9}/>
                  <stop offset="50%" stopColor="#2563eb" stopOpacity={0.6}/>
                  <stop offset="95%" stopColor="#1e40af" stopOpacity={0.2}/>
                </linearGradient>
                
                {/* Glowing effect for Download */}
                <filter id="downloadGlow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
                
                {/* Glowing effect for Upload */}
                <filter id="uploadGlow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              
              {/* Dark grid with neon glow */}
              <CartesianGrid 
                strokeDasharray="1 3" 
                stroke="#334155" 
                strokeOpacity={0.3}
                vertical={false}
              />
              
              <XAxis 
                dataKey="time" 
                tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 500 }}
                stroke="#475569"
                angle={-20}
                textAnchor="end"
                height={60}
                axisLine={{ stroke: '#475569', strokeWidth: 2 }}
              />
              
              <YAxis 
                label={{ 
                  value: 'Speed (Mbps)', 
                  angle: -90, 
                  position: 'insideLeft', 
                  style: { fontSize: 12, fill: '#94a3b8', fontWeight: 600 } 
                }}
                tick={{ fontSize: 11, fill: '#94a3b8', fontWeight: 500 }}
                stroke="#475569"
                axisLine={{ stroke: '#475569', strokeWidth: 2 }}
              />
              
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  border: '2px solid #10b981',
                  borderRadius: '12px',
                  fontSize: '12px',
                  color: '#fff',
                  padding: '12px 16px',
                  boxShadow: '0 10px 40px rgba(16, 185, 129, 0.3)'
                }}
                labelStyle={{ 
                  color: '#10b981', 
                  fontWeight: 'bold', 
                  marginBottom: '8px',
                  fontSize: '13px'
                }}
                itemStyle={{ 
                  color: '#e2e8f0',
                  padding: '4px 0',
                  fontWeight: 500
                }}
              />
              
              <Legend 
                wrapperStyle={{ 
                  fontSize: '13px', 
                  paddingTop: '15px',
                  fontWeight: 600
                }}
                iconType="circle"
                iconSize={10}
              />
              
              {/* Download Area - Green with glow */}
              <Area 
                type="monotoneX" 
                dataKey="download" 
                stroke="#10b981" 
                strokeWidth={3}
                fill="url(#downloadGradient)"
                name="⬇️ Download"
                dot={{ fill: '#10b981', r: 3, strokeWidth: 0 }}
                activeDot={{ 
                  r: 6, 
                  fill: '#10b981',
                  stroke: '#fff',
                  strokeWidth: 2,
                  filter: 'url(#downloadGlow)'
                }}
                animationDuration={1000}
              />
              
              {/* Upload Area - Blue with glow */}
              <Area 
                type="monotoneX" 
                dataKey="upload" 
                stroke="#3b82f6" 
                strokeWidth={3}
                fill="url(#uploadGradient)"
                name="⬆️ Upload"
                dot={{ fill: '#3b82f6', r: 3, strokeWidth: 0 }}
                activeDot={{ 
                  r: 6, 
                  fill: '#3b82f6',
                  stroke: '#fff',
                  strokeWidth: 2,
                  filter: 'url(#uploadGlow)'
                }}
                animationDuration={1000}
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Ultra Compact Customer Breakdown */}
      {viewMode === 'customer' && customerData.length > 0 && (
        <Card className="shadow-md border border-blue-200">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-600 p-2">
            <div className="flex items-center gap-2">
              <div className="bg-white rounded p-1">
                <Users className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-sm font-bold text-white">
                  Customer Traffic Breakdown
                </CardTitle>
                <p className="text-xs text-white/80">Real-time usage</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={customerData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="barDownload" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={1}/>
                    <stop offset="100%" stopColor="#059669" stopOpacity={0.8}/>
                  </linearGradient>
                  <linearGradient id="barUpload" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={1}/>
                    <stop offset="100%" stopColor="#2563eb" stopOpacity={0.8}/>
                  </linearGradient>
                </defs>
                
                <CartesianGrid 
                  strokeDasharray="1 3" 
                  stroke="#334155" 
                  strokeOpacity={0.3}
                  vertical={false}
                />
                
                <XAxis 
                  dataKey="customer_name" 
                  tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 500 }}
                  stroke="#475569"
                  angle={-25}
                  textAnchor="end"
                  height={90}
                  axisLine={{ stroke: '#475569', strokeWidth: 2 }}
                />
                
                <YAxis 
                  label={{ 
                    value: 'Speed (Mbps)', 
                    angle: -90, 
                    position: 'insideLeft', 
                    style: { fontSize: 12, fill: '#94a3b8', fontWeight: 600 } 
                  }}
                  tick={{ fontSize: 11, fill: '#94a3b8', fontWeight: 500 }}
                  stroke="#475569"
                  axisLine={{ stroke: '#475569', strokeWidth: 2 }}
                />
                
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(15, 23, 42, 0.95)',
                    border: '2px solid #3b82f6',
                    borderRadius: '12px',
                    fontSize: '12px',
                    color: '#fff',
                    padding: '12px 16px',
                    boxShadow: '0 10px 40px rgba(59, 130, 246, 0.3)'
                  }}
                  labelStyle={{ 
                    color: '#3b82f6', 
                    fontWeight: 'bold', 
                    marginBottom: '8px',
                    fontSize: '13px'
                  }}
                  itemStyle={{ 
                    color: '#e2e8f0',
                    padding: '4px 0',
                    fontWeight: 500
                  }}
                />
                
                <Legend 
                  wrapperStyle={{ 
                    fontSize: '13px', 
                    paddingTop: '15px',
                    fontWeight: 600
                  }} 
                  iconType="circle"
                  iconSize={10}
                />
                
                <Bar 
                  dataKey="download_mbps" 
                  fill="url(#barDownload)" 
                  name="⬇️ Download" 
                  radius={[6, 6, 0, 0]}
                  animationDuration={1000}
                />
                <Bar 
                  dataKey="upload_mbps" 
                  fill="url(#barUpload)" 
                  name="⬆️ Upload" 
                  radius={[6, 6, 0, 0]}
                  animationDuration={1000}
                />
              </BarChart>
            </ResponsiveContainer>

            {/* Ultra Compact Customer List */}
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
              {customerData.map((customer, index) => (
                <div 
                  key={customer.customer_id}
                  className="bg-white border border-gray-200 rounded p-2 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-bold text-xs text-gray-900 truncate">{customer.customer_name}</h3>
                    <Monitor className="h-3 w-3 text-blue-600 flex-shrink-0" />
                  </div>
                  <p className="text-xs text-gray-500 mb-1 truncate">{customer.static_ip}</p>
                  <div className="space-y-0.5">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">Download:</span>
                      <span className="text-xs font-bold text-red-600">{customer.download_mbps.toFixed(1)} Mbps</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">Upload:</span>
                      <span className="text-xs font-bold text-yellow-600">{customer.upload_mbps.toFixed(1)} Mbps</span>
                    </div>
                    <div className="flex justify-between items-center pt-0.5 border-t">
                      <span className="text-xs font-medium text-gray-700">Total:</span>
                      <span className="text-xs font-bold text-purple-600">{customer.total_mbps.toFixed(1)} Mbps</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LiveTrafficMonitor;
